/**
 * ENG-7577 — reconcile a held circuit-breaker trip against the AUTHORITATIVE
 * agent status, so an operator can always clear a tripped breaker from the
 * console without host access.
 *
 * The bug this exists for. A trip (ENG-5441) is a LATCH: `RestartBreaker.trips`
 * is only ever emptied by `clear()`, and `isTripped()` never consults the clock.
 * The one call site that clears on operator intent is EDGE-triggered — the
 * manager must personally witness a `paused → active` transition, comparing
 * against `agentState.knownStatuses`. That map is in-memory and starts EMPTY on
 * every manager (re)start, while trips are PERSISTED in `manager-state.json` and
 * rehydrated. So whenever the manager restarts across an operator's resume — or
 * simply hasn't observed the agent as `paused` in this worker generation — the
 * edge never fires, the hydrated trip latches forever, and every poll logs
 * `decision=circuit-tripped spawn_attempted=false`. Worse, the hydrated trip is
 * re-reported to the API, which re-pauses the agent the operator just resumed.
 *
 * That is exactly how `don` (a customer's FIRST agent, on its first day) stayed
 * dead for 3h+ on 28 Jul with no supported recovery path: the console's Resume
 * demonstrably did not reach `clear()`, no admin action exposes it, and the only
 * fix was hand-editing `manager-state.json` on the host — itself racy, because a
 * still-running manager serialises its in-memory trip back over the edit.
 *
 * The fix is to stop depending on witnessing an edge. Pause state is owned by
 * the control plane (`agents.status`), not by the manager: if the authoritative
 * status reads `active` while this manager still holds a trip, the pause that
 * trip caused is no longer in force, and the trip is STALE. Level-triggered, so
 * it self-heals on the very next poll regardless of what the manager did or
 * didn't witness, and regardless of manager restarts.
 *
 * The one race to respect is the trip's own settling window: between `record()`
 * tripping and the API applying `status='paused'`, the agent legitimately still
 * reads `active`, and clearing then would defeat the breaker on the very tick it
 * fired. `settleMs` (default 2 min, ≫ the report + pause round-trip) bounds that
 * window. After it, a still-`active` agent means one of exactly two things, and
 * BOTH want the trip dropped:
 *
 *   1. an operator (or the auto-resume path) resumed it — honour their intent;
 *   2. the control plane never applied the pause at all (auto-pause disabled for
 *      the org) — in which case a manager unilaterally holding the agent dead
 *      forever is itself the defect.
 *
 * Pure decision, no I/O — manager-worker.ts owns the status read and the
 * `restartBreaker.clear()`, matching auto-resume.ts / resume-reconciler.ts.
 * Deliberately NOT flag-gated: this is a correctness fix to an existing recovery
 * path, not new behaviour to bake. Its only tunable is the settle window.
 */

import type { TripState } from './restart-breaker.js';
import { readEnvNumber } from './restart-breaker.js';

/** Default settle window — well beyond the trip-report → status-paused round trip. */
const DEFAULT_STALE_TRIP_SETTLE_MS = 120_000; // 2 min
/**
 * Floor, matching RestartBreaker's own `windowMs >= 1000` guard. `readEnvNumber`
 * already rejects NaN and <= 0, but a fat-fingered `...SETTLE_MS=5` would leave a
 * 5 ms window — effectively no settle at all, which is exactly the race this
 * module exists to respect. Clamp rather than throw: an unusable settle window
 * must not take the manager down at boot.
 */
const MIN_STALE_TRIP_SETTLE_MS = 1_000;

export function readStaleTripSettleMs(): number {
  return Math.max(
    MIN_STALE_TRIP_SETTLE_MS,
    readEnvNumber('AGT_RESTART_BREAKER_STALE_TRIP_SETTLE_MS', DEFAULT_STALE_TRIP_SETTLE_MS),
  );
}

export type StaleTripDecision =
  | { action: 'clear'; ageMs: number }
  | {
      action: 'hold';
      reason: 'no-trip' | 'pause-in-force' | 'settling';
      /** For 'settling': ms until the settle window closes. */
      remainingMs?: number;
    };

/**
 * Decide whether a held trip has been superseded by the authoritative status.
 *
 * `agentStatus` is the status from `/host/agents` — the control plane's answer,
 * including any server-side overlay. `killSwitched` is checked separately
 * because a kill-switch pause is re-applied by the API on every poll: clearing a
 * trip there would be pointless churn, and the kill switch (not the breaker) is
 * what holds the agent down.
 */
export function decideStaleTripClear(opts: {
  trip: TripState | undefined;
  agentStatus: string;
  killSwitched: boolean;
  settleMs: number;
  now: number;
}): StaleTripDecision {
  const { trip, agentStatus, killSwitched, settleMs, now } = opts;
  if (!trip) return { action: 'hold', reason: 'no-trip' };
  // Anything other than an authoritative `active` means a pause IS in force
  // (breaker, operator, cost, kill switch) or the agent is not runnable at all
  // (draft / revoked). Only `active` proves the pause was lifted.
  if (agentStatus !== 'active' || killSwitched) {
    return { action: 'hold', reason: 'pause-in-force' };
  }
  const ageMs = now - trip.trippedAt;
  if (ageMs < settleMs) {
    return { action: 'hold', reason: 'settling', remainingMs: settleMs - ageMs };
  }
  return { action: 'clear', ageMs };
}
